import React, { useEffect } from 'react';
import CountdownTimer from './CountdownTimer';
import CTAButton from './CTAButton';
import LazyVideo from './LazyVideo';
import { logScrollContainers } from '../utils/findScrollContainers';
import { trackLandingPageVideo } from '../utils/analytics';
import useGTM from '../hooks/useGTM';
import { pushToDataLayer } from '../config/gtm';

const LandingPageEN: React.FC = () => {
    // Google Tag Manager hook
    const { trackCTA, trackVideo, trackScroll, trackAction } = useGTM();

    // Configure language and page variables in GTM so only English pixel fires
    useEffect(() => {
        // Send variables to dataLayer to identify this is the English landing page
        pushToDataLayer({
            page_language: 'en',
            page_type: 'landing_en',
            page_path: '/landingpageEN',
            pixel_language: 'en' // Specific variable to control which pixel to fire
        });
    }, []);

    // Automatic scroll containers checker (development only)
    useEffect(() => {
        if (process.env.NODE_ENV !== 'production') {
            // Execute after DOM is fully loaded
            const timer = setTimeout(() => {
                logScrollContainers();
            }, 1000);
            
            return () => clearTimeout(timer);
        }
    }, []);

    // Video tracking functions (combining GA and GTM)
    const handleVideoPlay = (videoName: string, section: string) => {
        // Tracking with Google Analytics (existing)
        trackLandingPageVideo(videoName, section);
        
        // Additional tracking with Google Tag Manager
        trackVideo('play', videoName, section);
    };

    // Function to track CTA clicks
    const handleCTAClick = (ctaName: string, section: string) => {
        trackCTA(ctaName, section, 'button');
    };

    // Function to track scroll depth
    const handleScrollDepth = (depth: number) => {
        trackScroll(depth);
    };

    // Function to track specific actions
    const handleUserAction = (action: string, category: string, label: string) => {
        trackAction(action, category, label);
    };

    return (
        <div id="app-root" className="relative flex min-h-[100svh] flex-col bg-yellow-50 w-full overflow-x-clip">
            {/* Countdown Timer */}
            <CountdownTimer />
            
            {/* Header */}
            <header className="bg-yellow-50 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-3 sm:py-4">
                        <div className="flex items-center space-x-2 sm:space-x-4">
                            <img 
                                src="/images/princonser kids logo.png" 
                                alt="Princonser Kids Logo" 
                                className="h-10 sm:h-12 md:h-14 w-auto"
                            />
                            <h1 className="hidden sm:block text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-900 leading-tight">
                                First Immersive Comprehensive Education Experience
                            </h1>
                        </div>
                    </div>
                </div>
            </header>

            <main className="flex-1">
                {/* Hero Section */}
            <section 
                id="inicio" 
                className="relative min-h-[100svh] flex items-center justify-center overflow-hidden"
                style={{
                    background: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url('/images/landingpage/FondoHeroLandingpage.png')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                {/* Visible hero content */}
                <div className="relative z-10 max-w-7xl w-full px-4 sm:px-6 lg:px-8">
                    <div className="text-center text-white">
                        {/* Improved main title */}
                        <div className="mb-6 sm:mb-8">
                            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl xl:text-7xl font-bold mb-4 sm:mb-6 leading-tight drop-shadow-2xl">
                                <span className="text-yellow-300">First Immersive</span><br/>
                                <span className="text-white">Comprehensive Education</span><br/>
                                <span className="text-yellow-300">Experience Based on</span><br/>
                                <span className="text-white">Wisdom</span>
                            </h1>
                            <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-gray-200 mb-6 sm:mb-8 max-w-4xl mx-auto leading-relaxed drop-shadow-lg px-2">
                                <strong>Transform your children's future</strong> with the only methodology that combines classic stories, cognitive development, and financial education
                            </p>
                        </div>

                        {/* Improved Video Section */}
                        <div className="w-full mb-6 sm:mb-8 max-w-5xl mx-auto">
                            <div className="relative w-full bg-black/80 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border-2 sm:border-4 border-yellow-300">
                                <LazyVideo
                                    src="/videos/landingpage/finalVE.mp4"
                                    poster="/images/landingpage/video-poster.jpg"
                                    className="w-full h-auto"
                                    videoId="hero-video-en"
                                    onPlay={() => handleVideoPlay('finalVE', 'Hero Section')}
                                />
                            </div>
                            
                            {/* CTA Button centered below video */}
                            <div className="mt-4 sm:mt-8 text-center px-4">
                                <CTAButton 
                                    ctaName="Take the first step in your children's comprehensive education"
                                    section="Hero Section"
                                    as="a"
                                    href="https://www.amazon.com/dp/B0FX2BBP1D?ref_=pe_93986420_774957520"
                                >
                                    Take the first step in your children's comprehensive education
                                </CTAButton>
                                <p className="text-xs sm:text-sm text-gray-300 mt-2 sm:mt-4">
                                    ✅ Satisfaction guarantee • ✅ 24/7 Support
                                </p>
                            </div>
                        </div>
                        
                        {/* Improved Endorsements */}
                        <div className="bg-white/50 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 max-w-4xl mx-auto shadow-lg">
                            <p className="text-xs sm:text-sm text-gray-800 mb-3 sm:mb-4 font-semibold uppercase tracking-wide">Endorsed by recognized institutions:</p>
                            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 lg:space-x-8">
                                <div className="flex items-center space-x-2 sm:space-x-3">
                                    <img 
                                        src="/images/ForoFilosofia.png" 
                                        alt="World Philosophical Forum" 
                                        className="h-8 sm:h-10 lg:h-12 w-auto opacity-90 hover:opacity-100 transition-opacity"
                                    />
                                    <div className="text-left">
                                        <div className="text-black font-semibold text-xs sm:text-sm">World Philosophical Forum</div>
                                        <div className="text-gray-600 text-xs">International Institution</div>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2 sm:space-x-3">
                                    <img 
                                        src="/images/PrinconserUniversity.png" 
                                        alt="Princonser University" 
                                        className="h-8 sm:h-10 lg:h-12 w-auto opacity-90 hover:opacity-100 transition-opacity"
                                    />
                                    <div className="text-left">
                                        <div className="text-black font-semibold text-xs sm:text-sm">Princonser University</div>
                                        <div className="text-gray-600 text-xs">Certified University</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Jean Piaget Section */}
            <section 
                className="py-12 sm:py-16 lg:py-20 relative"
                style={{
                    background: `linear-gradient(rgba(255,255,255,0.8), rgba(255,255,255,0.8)), url('/images/landingpage/FondoSection2Landingpage.png')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-8 sm:mb-12">
                        <div className="inline-block bg-red-100 text-red-800 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
                            🧠 SCIENTIFIC FOUNDATION
                        </div>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6 px-2">
                            Why do we focus on 
                            <span className="text-red-600"> primary education</span> children?
                        </h2>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12">
                        {/* Text Content */}
                        <div className="space-y-4 sm:space-y-6">
                            <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-xl border border-red-200">
                                <div className="flex items-center mb-3 sm:mb-4">
                                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center mr-3 sm:mr-4">
                                        <span className="text-xl sm:text-2xl">🧠</span>
                                    </div>
                                    <h3 className="text-xl sm:text-2xl font-bold text-red-600">Jean Piaget</h3>
                                </div>
                                <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed mb-3 sm:mb-4">
                                    <span className="font-bold text-red-600">According to</span> Jean Piaget (1896-1980), considered <span className="font-bold text-red-600">the Father of Developmental Psychology</span>, the stage in children from seven to eleven years old is <span className="font-bold text-red-600">the most important for cognitive development</span>.
                                </p>
                                <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                                    This is when they learn to use logic and reason about concrete and tangible events.
                                </p>
                            </div>
                            
                            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-l-4 border-yellow-400">
                                <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">🎯 Window of Opportunity</h4>
                                <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                                    If you have children in Primary Education (ages 7 to 12), allow them to begin the journey toward wisdom at the most critical moment of their development.
                                </p>
                            </div>
                            
                            <div className="flex justify-center lg:justify-start">
                                <CTAButton 
                                    ctaName="I want to increase my children's mental development"
                                    section="Jean Piaget Section"
                                    as="a"
                                    href="https://www.amazon.com/dp/B0FX2BBP1D?ref_=pe_93986420_774957520"
                                >
                                    I want to increase my children's mental development
                                </CTAButton>
                            </div>
                        </div>
                        
                        {/* Jean Piaget Illustration */}
                        <div className="flex justify-center lg:justify-end">
                            <div className="relative">
                                <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-2xl max-w-sm transform hover:scale-105 transition-transform duration-300">
                                    <img 
                                        src="/images/JeanPiaget.png" 
                                        alt="Jean Piaget" 
                                        className="w-full h-auto object-contain mb-3 sm:mb-4"
                                    />
                                    <div className="text-center">
                                        <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 sm:mb-2">Jean Piaget</h3>
                                        <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">Father of Developmental Psychology</p>
                                        <div className="bg-red-50 rounded-lg p-2 sm:p-3">
                                            <p className="text-xs text-red-700 font-semibold">
                                                "Intelligence is what you use when you don't know what to do"
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                {/* Decorative effect */}
                                <div className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 w-6 h-6 sm:w-8 sm:h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                                    <span className="text-white text-xs sm:text-sm">✨</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Parents Concern Section */}
            <section 
                className="py-12 sm:py-16 lg:py-20 relative"
                style={{
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-8 sm:mb-12">
                        <div className="inline-block bg-orange-100 text-orange-800 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
                            😰 ALARMING STATISTICS
                        </div>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 px-2">
                            Today's parents' 
                            <span className="text-yellow-300"> first concern</span>
                        </h2>
                    </div>
                    
                    <div className="flex flex-col lg:flex-row items-center gap-4 sm:gap-6 lg:gap-8">
                        {/* Family Illustration */}
                        <div className="flex-shrink-0 w-full lg:w-auto">
                            <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-3 sm:p-4 lg:p-6 shadow-2xl max-w-xs sm:max-w-sm mx-auto transform hover:scale-105 transition-transform duration-300 w-full">
                                <img 
                                    src="/images/PreocupacionPadres.png" 
                                    alt="Family concerned about their children's mental health" 
                                    className="w-full h-auto object-contain"
                                />
                            </div>
                        </div>
                        
                        {/* Statistics */}
                        <div className="flex-1 min-w-0 w-full">
                            <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-2xl">
                                <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2 sm:mb-3 lg:mb-4 drop-shadow-lg">55%</div>
                                <p className="text-xs sm:text-sm lg:text-base text-white leading-relaxed font-semibold">
                                    Of parents in the United States say their main concern is their children's 
                                    <span className="text-yellow-300"> Mental Health</span> 
                                    (anxiety and depression).
                                </p>
                            </div>
                        </div>
                        
                        {/* Video Testimonial */}
                        <div className="flex-shrink-0 w-full lg:w-auto">
                            <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-3 sm:p-4 lg:p-6 shadow-2xl border-2 sm:border-4 border-yellow-300 max-w-sm sm:max-w-md lg:max-w-lg mx-auto w-full">
                                <div className="relative w-full bg-black/80 rounded-xl sm:rounded-2xl overflow-hidden shadow-xl">
                                    <LazyVideo
                                        src="/videos/landingpage/VE Clip v.1.mp4"
                                        poster="/images/landingpage/video-poster.jpg"
                                        className="w-full h-auto"
                                        videoId="parents-video-en"
                                        onPlay={() => handleVideoPlay('VE Clip v.1', 'Parents Concern Section')}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* CTA Button */}
                    <div className="text-center mt-8 sm:mt-12">
                        <CTAButton 
                            ctaName="I want my child to start the path to wisdom today"
                            section="Parents Concern Section"
                            as="a"
                            href="https://www.amazon.com/dp/B0FX2BBP1D?ref_=pe_93986420_774957520"
                        >
                            I want my child to start the path to wisdom today
                        </CTAButton>
                        <div className="bg-black/60 backdrop-blur-sm rounded-lg px-4 py-2 mt-2 sm:mt-4 inline-block border border-white/20">
                            <p className="text-white text-xs sm:text-sm font-medium">
                                🔒 Your information is 100% protected • 📞 Personalized support included
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Financial Concern Section */}
            <section 
                className="py-12 sm:py-16 lg:py-20 relative"
                style={{
                    backgroundImage: `linear-gradient(rgba(255,255,255,0.3), rgba(255,255,255,0.3)), url('/images/landingpage/FondoSection3Landingpage.png')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-8 sm:mb-12">
                        <div className="inline-block bg-green-100 text-green-800 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
                            💰 FINANCIAL STATISTICS
                        </div>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6 px-2">
                            Financial concern for 
                            <span className="text-green-600"> your children</span>
                        </h2>
                    </div>

                    <div className="flex flex-col lg:flex-row items-start gap-4 sm:gap-6 lg:gap-8">
                        {/* Video Success Case - Left Side */}
                        <div className="flex-shrink-0 w-full lg:w-auto mt-4 sm:mt-6 lg:mt-8">
                            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl sm:rounded-3xl p-4 sm:p-5 lg:p-7 text-center shadow-2xl border-2 sm:border-4 border-green-300 max-w-md sm:max-w-lg lg:max-w-xl mx-auto w-full">
                                <div className="relative w-full bg-black/80 rounded-xl sm:rounded-2xl overflow-hidden shadow-xl">
                                    <LazyVideo
                                        src="/videos/landingpage/E1 Clip v.1.mp4"
                                        poster="/images/landingpage/video-poster.jpg"
                                        className="w-full h-auto"
                                        videoId="financial-video-en"
                                        onPlay={() => handleVideoPlay('E1 Clip v.1', 'Financial Concern Section')}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Statistics and Image */}
                        <div className="flex-1 min-w-0 space-y-4 sm:space-y-6 lg:space-y-8 w-full">
                            {/* Statistics */}
                            <div className="bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-2xl">
                                <div className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-2 sm:mb-3 lg:mb-4 drop-shadow-lg">9 out of 10</div>
                                <p className="text-xs sm:text-sm lg:text-base text-white leading-relaxed font-semibold">
                                    parents say it is <span className="text-yellow-300">'extremely'</span> important or <span className="text-yellow-300">'very'</span> important that their children be financially independent.
                                </p>
                            </div>

                            {/* Family Illustration */}
                            <div className="flex justify-center lg:justify-start">
                                <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-3 sm:p-4 lg:p-6 shadow-2xl max-w-xs sm:max-w-sm mx-auto transform hover:scale-105 transition-transform duration-300 w-full">
                                    <img
                                        src="/images/FuturoEconomico.png"
                                        alt="Parents concerned about their children's economic future"
                                        className="w-full h-auto object-contain"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CTA Button */}
                    <div className="text-center mt-8 sm:mt-12">
                        <CTAButton 
                            ctaName="I want my children to have a promising future"
                            section="Financial Concern Section"
                            as="a"
                            href="https://www.amazon.com/dp/B0FX2BBP1D?ref_=pe_93986420_774957520"
                        >
                            I want my children to have a promising future
                        </CTAButton>
                        <div className="mt-2 sm:mt-4">
                            <div className="bg-black/60 backdrop-blur-sm rounded-lg px-4 py-2 inline-block border border-white/20">
                                <p className="text-white text-xs sm:text-sm font-medium">
                                    🎯 Practical financial education • 💡 Real projects • 🏆 Certification included
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Purpose Importance Section */}
            <section 
                className="py-12 sm:py-16 lg:py-20 relative"
                style={{
                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-8 sm:mb-12">
                        <div className="inline-block bg-purple-100 text-purple-800 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
                            🎯 SCIENTIFIC FOUNDATION
                        </div>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 px-2">
                            Importance of a 
                            <span className="text-purple-300"> purpose</span>
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                        {/* Left Column - Illustration */}
                        <div className="flex justify-center lg:justify-start">
                            <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-2 sm:p-3 lg:p-4 shadow-2xl max-w-xs sm:max-w-sm transform hover:scale-105 transition-transform duration-300 w-full">
                                <img
                                    src="/images/MultiplesEstudios.png"
                                    alt="Child pointing with purpose"
                                    className="w-full h-auto object-contain"
                                />
                            </div>
                        </div>

                        {/* Right Column - Text Content and Video */}
                        <div className="space-y-4 sm:space-y-6">
                            {/* Text Content */}
                            <div className="bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-5 lg:p-6 border border-purple-300 w-full shadow-lg">
                                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 mb-3 sm:mb-4 text-center lg:text-left">
                                    Multiple studies
                                </h3>
                                <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed text-center lg:text-left">
                                    conclude that having a purpose helps regulate stress and significantly improves <span className="text-purple-600 font-bold">Mental Health</span>.
                                </p>
                            </div>

                            {/* Video */}
                            <div className="flex justify-center lg:justify-start">
                                <div className="w-full max-w-sm sm:max-w-md lg:max-w-lg">
                                    <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-3 sm:p-4 lg:p-5 shadow-2xl border-2 sm:border-4 border-purple-300">
                                        <div className="relative w-full bg-black/80 rounded-xl sm:rounded-2xl overflow-hidden shadow-xl">
                                            <LazyVideo
                                                src="/videos/landingpage/E2 v.1.mp4"
                                                poster="/images/landingpage/video-poster.jpg"
                                                className="w-full h-auto"
                                                videoId="purpose-video-en"
                                                onPlay={() => handleVideoPlay('E2 v.1', 'Purpose Importance Section')}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CTA Button */}
                    <div className="text-center mt-8 sm:mt-12">
                        <CTAButton 
                            ctaName="I want my children to acquire purpose"
                            section="Purpose Importance Section"
                            as="a"
                            href="https://www.amazon.com/dp/B0FX2BBP1D?ref_=pe_93986420_774957520"
                        >
                            I want my children to acquire purpose
                        </CTAButton>
                        <div className="mt-2 sm:mt-4">
                            <div className="bg-black/60 backdrop-blur-sm rounded-lg px-4 py-2 inline-block border border-white/20">
                                <p className="text-white text-xs sm:text-sm font-medium">
                                    📚 Proven methodology • 🧠 Cognitive development • 💪 Mental strength
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Goal is Clear Section */}
            <section 
                className="py-12 sm:py-16 lg:py-20 relative"
                style={{
                    background: `linear-gradient(rgba(240, 147, 251, 0.8), rgba(245, 87, 108, 0.8)), url('/images/landingpage/FondoNuestra.png')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                        <div className="inline-block bg-yellow-100 text-yellow-800 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
                            🎯 OUR MISSION
                        </div>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-6 sm:mb-8 lg:mb-12 px-2">
                            Our goal is 
                            <span className="text-yellow-600"> clear</span>
                        </h2>
                        
                        {/* Book Covers Row - Single Row */}
                        <div className="flex justify-center items-center gap-2 sm:gap-3 mb-8 sm:mb-12 lg:mb-16 overflow-x-auto overflow-y-hidden px-4">
                            {/* Book 1 */}
                            <div className="flex-shrink-0">
                                <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 border-2 border-yellow-200">
                                    <img 
                                        src="/images/portadas/portadalibro1.png" 
                                        alt="Book 1" 
                                        className="w-16 h-20 sm:w-20 sm:h-24 object-cover rounded-md sm:rounded-lg"
                                    />
                                </div>
                            </div>

                            {/* Book 2 */}
                            <div className="flex-shrink-0">
                                <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 border-2 border-yellow-200">
                                    <img 
                                        src="/images/portadas/portadalibro2.png" 
                                        alt="Book 2" 
                                        className="w-16 h-20 sm:w-20 sm:h-24 object-cover rounded-md sm:rounded-lg"
                                    />
                                </div>
                            </div>

                            {/* Book 3 */}
                            <div className="flex-shrink-0">
                                <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 border-2 border-yellow-200">
                                    <img 
                                        src="/images/portadas/portadalibro3.png" 
                                        alt="Book 3" 
                                        className="w-16 h-20 sm:w-20 sm:h-24 object-cover rounded-md sm:rounded-lg"
                                    />
                                </div>
                            </div>

                            {/* Book 4 */}
                            <div className="flex-shrink-0">
                                <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 border-2 border-yellow-200">
                                    <img 
                                        src="/images/portadas/portadalibro4.png" 
                                        alt="Book 4" 
                                        className="w-16 h-20 sm:w-20 sm:h-24 object-cover rounded-md sm:rounded-lg"
                                    />
                                </div>
                            </div>

                            {/* Book 5 */}
                            <div className="flex-shrink-0">
                                <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 border-2 border-yellow-200">
                                    <img 
                                        src="/images/portadas/portadalibro5.png" 
                                        alt="Book 5" 
                                        className="w-16 h-20 sm:w-20 sm:h-24 object-cover rounded-md sm:rounded-lg"
                                    />
                                </div>
                            </div>

                            {/* Book 6 */}
                            <div className="flex-shrink-0">
                                <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 border-2 border-yellow-200">
                                    <img 
                                        src="/images/portadas/portadalibro6.png" 
                                        alt="Book 6" 
                                        className="w-16 h-20 sm:w-20 sm:h-24 object-cover rounded-md sm:rounded-lg"
                                    />
                                </div>
                            </div>

                            {/* Book 7 */}
                            <div className="flex-shrink-0">
                                <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 border-2 border-yellow-200">
                                    <img 
                                        src="/images/portadas/portadalibro7.png" 
                                        alt="Book 7" 
                                        className="w-16 h-20 sm:w-20 sm:h-24 object-cover rounded-md sm:rounded-lg"
                                    />
                                </div>
                            </div>

                            {/* Book 8 */}
                            <div className="flex-shrink-0">
                                <div className="bg-white rounded-lg sm:rounded-xl p-2 sm:p-3 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 border-2 border-yellow-200">
                                    <img 
                                        src="/images/portadas/portadalibro8.png" 
                                        alt="Book 8" 
                                        className="w-16 h-20 sm:w-20 sm:h-24 object-cover rounded-md sm:rounded-lg"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Two Column Content */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
                            {/* Left Column - Wisdom */}
                            <div className="text-left">
                                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl border-l-4 border-yellow-400">
                                    <div className="flex items-center mb-4 sm:mb-6">
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-yellow-400 rounded-full flex items-center justify-center mr-3 sm:mr-4">
                                            <span className="text-2xl sm:text-3xl">🧠</span>
                                        </div>
                                        <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800">
                                            Wisdom
                                        </h3>
                                    </div>
                                    <div className="space-y-3 sm:space-y-4 text-gray-700">
                                        <p className="text-sm sm:text-base lg:text-lg leading-relaxed">
                                            A child can demonstrate greater wisdom than an adult, as this has nothing to do with age or experience.
                                        </p>
                                        <p className="text-sm sm:text-base lg:text-lg leading-relaxed">
                                            We guarantee the acquisition of wisdom in your children, which will allow them to win any debate at home and at school.
                                        </p>
                                    </div>
                                    <div className="mt-4 sm:mt-6 bg-yellow-100 rounded-lg sm:rounded-xl p-3 sm:p-4">
                                        <h4 className="text-base sm:text-lg font-bold text-gray-800 mb-1 sm:mb-2">🏆 Tangible Results</h4>
                                        <p className="text-sm sm:text-base text-gray-700">Positive habit changes, titles and badges</p>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Purpose */}
                            <div className="text-left">
                                <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl border-l-4 border-green-400">
                                    <div className="flex items-center mb-4 sm:mb-6">
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-400 rounded-full flex items-center justify-center mr-3 sm:mr-4">
                                            <span className="text-2xl sm:text-3xl">💰</span>
                                        </div>
                                        <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800">
                                            Purpose
                                        </h3>
                                    </div>
                                    <div className="space-y-3 sm:space-y-4 text-gray-700">
                                        <p className="text-sm sm:text-base lg:text-lg leading-relaxed">
                                            Our goal is the financial education of your children. At the end of the experience, they will receive funding or guidance to obtain resources for an entrepreneurship or professional vocation.
                                        </p>
                                    </div>
                                    <div className="mt-4 sm:mt-6 bg-green-100 rounded-lg sm:rounded-xl p-3 sm:p-4">
                                        <h4 className="text-base sm:text-lg font-bold text-gray-800 mb-1 sm:mb-2">🏆 Tangible Results</h4>
                                        <p className="text-sm sm:text-base text-gray-700">Funding, economic bonuses, real entrepreneurship, vocational motivation</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* CTA Button */}
                        <div className="text-center mt-8 sm:mt-12">
                            <CTAButton 
                                ctaName="I want my children to start a transformative experience"
                                section="Our Goal Section"
                                as="a"
                                href="https://www.amazon.com/dp/B0FX2BBP1D?ref_=pe_93986420_774957520"
                            >
                                I want my children to start a transformative experience
                            </CTAButton>
                            <div className="bg-black/60 backdrop-blur-sm rounded-lg px-4 py-2 mt-2 sm:mt-4 inline-block border border-white/20">
                                <p className="text-white text-xs sm:text-sm font-medium">
                                    🎯 8 unique books • 💡 Proven methodology • 🏆 Guaranteed results
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Book Structure Section */}
            <section 
                className="py-12 sm:py-16 lg:py-20 relative"
                style={{
                    background: `linear-gradient(rgba(255,255,255,0.9), rgba(255,255,255,0.9)), url('/images/landingpage/FondoExperiencia.png')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-6 sm:mb-8">
                        <div className="inline-block bg-blue-100 text-blue-800 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
                            📚 UNIQUE METHODOLOGY
                        </div>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6 px-2">
                            Structure of our 
                            <span className="text-blue-600"> books</span>
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
                        {/* Left Column - Book Cover and Activities */}
                        <div className="flex justify-center lg:justify-start">
                            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                                {/* Children Activities Illustration - Left Side */}
                                <div className="bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl p-2 sm:p-3 shadow-xl max-w-40 sm:max-w-48 transform hover:scale-105 transition-transform duration-300 order-2 sm:order-1">
                                    <img 
                                        src="/images/Actividades de bondad infantil.png" 
                                        alt="Children performing kindness activities" 
                                        className="w-full h-auto object-contain"
                                    />
                                </div>
                                
                                {/* Book Cover */}
                                <div className="relative order-1 sm:order-2">
                                    <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-2xl max-w-sm sm:max-w-lg transform hover:scale-105 transition-transform duration-300 border-2 sm:border-4 border-blue-200">
                                        <img 
                                            src="/images/portadas/portadalibro1.png" 
                                            alt="Book 1 - Learning to Learn" 
                                            className="w-full h-auto object-contain"
                                        />
                                    </div>
                                    {/* Decorative effect */}
                                    <div className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 lg:-top-4 lg:-right-4 w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12 bg-blue-400 rounded-full flex items-center justify-center">
                                        <span className="text-white text-xs sm:text-sm lg:text-lg">📖</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Content */}
                        <div className="space-y-4 sm:space-y-6 lg:space-y-8">
                            {/* Children's Story */}
                            <div className="space-y-3 sm:space-y-4">
                                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl border-l-4 border-yellow-400">
                                    <div className="flex items-center mb-3 sm:mb-4">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-400 rounded-full flex items-center justify-center mr-3 sm:mr-4">
                                            <span className="text-xl sm:text-2xl">📖</span>
                                        </div>
                                        <h3 className="text-xl sm:text-2xl font-bold text-gray-800">
                                            Children's Story
                                        </h3>
                                    </div>
                                    <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                                        Through a children's story, we convey a reflective message about the central theme of the book. In the first one, the story of "The Emperor's New Clothes" emphasizes the importance of knowledge and truth.
                                    </p>
                                </div>
                            </div>

                            {/* Metacognitive Development */}
                            <div className="space-y-3 sm:space-y-4">
                                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl border-l-4 border-blue-400">
                                    <div className="flex items-center mb-3 sm:mb-4">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-400 rounded-full flex items-center justify-center mr-3 sm:mr-4">
                                            <span className="text-xl sm:text-2xl">🧠</span>
                                        </div>
                                        <h3 className="text-xl sm:text-2xl font-bold text-gray-800">
                                            Metacognitive Development
                                        </h3>
                                    </div>
                                    <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                                        In a didactic way, the second part of the books presents cognitive development theories. This is accompanied by reflection and writing exercises that promote metacognition. In the first book, different types of knowledge are taught so your children learn to distinguish truth.
                                    </p>
                                </div>
                            </div>

                            {/* Experiential Experience */}
                            <div className="space-y-3 sm:space-y-4">
                                <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl border-l-4 border-green-400">
                                    <div className="flex items-center mb-3 sm:mb-4">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-400 rounded-full flex items-center justify-center mr-3 sm:mr-4">
                                            <span className="text-xl sm:text-2xl">🔬</span>
                                        </div>
                                        <h3 className="text-xl sm:text-2xl font-bold text-gray-800">
                                            Experiential Experience
                                        </h3>
                                    </div>
                                    <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                                        In the third part of our books, we include social and scientific experiments that allow children to experience firsthand the concepts explained in the book.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CTA Button */}
                    <div className="text-center mt-6 sm:mt-8">
                        <CTAButton 
                            ctaName="I want my children to Learn to Learn"
                            section="Book Structure Section"
                            as="a"
                            href="https://www.amazon.com/dp/B0FX2BBP1D?ref_=pe_93986420_774957520"
                        >
                            I want my children to Learn to Learn
                        </CTAButton>
                        <div className="mt-2 sm:mt-4">
                            <div className="bg-black/60 backdrop-blur-sm rounded-lg px-4 py-2 inline-block border border-white/20">
                                <p className="text-white text-xs sm:text-sm font-medium">
                                    🎯 Unique methodology • 📖 Classic stories • 🔬 Practical experiments
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            </main>
            
            <footer className="bg-gray-900 text-white py-8 sm:py-12 relative">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
                        <div className="md:col-span-2">
                            <div className="flex items-center mb-4 sm:mb-6">
                                <img 
                                    src="/images/princonser kids logo.png" 
                                    alt="Princonser Kids Logo" 
                                    className="h-10 sm:h-12 w-auto mr-3 sm:mr-4"
                                />
                                <div>
                                    <h3 className="text-lg sm:text-xl font-bold text-white">Princonser Kids</h3>
                                    <p className="text-yellow-300 text-xs sm:text-sm">Comprehensive Education</p>
                                </div>
                            </div>
                            <p className="text-gray-300 leading-relaxed mb-4 sm:mb-6 text-sm sm:text-base">
                                Transforming children's education with wisdom and technology. The only methodology that combines classic stories, cognitive development, and financial education for children ages 7-12.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                                <div className="bg-yellow-400 text-black px-3 py-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-bold">
                                    🏆 Internationally Endorsed
                                </div>
                                <div className="bg-green-400 text-black px-3 py-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-bold">
                                    📚 8 Unique Books
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="border-t border-gray-700 pt-6 sm:pt-8">
                        <div className="flex flex-col md:flex-row justify-between items-center">
                            <div className="text-gray-400 text-xs sm:text-sm mb-3 md:mb-0">
                                <p>&copy; 2024 Princonser Kids. All rights reserved.</p>
                                <p className="mt-1">Transforming the future of children's education.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPageEN;

